import { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { StatusSelector } from './StatusSelector';
import { SidebarTab } from './IconRail';
import {
    Hash,
    Settings,
    ChevronDown,
    ChevronRight,
    Plus,
    Search,
    Shield,
    Layers,
    Inbox,
    Calendar,
    Columns3,
    LayoutDashboard,
    Clock,
    FolderKanban
} from 'lucide-react';
import InviteResponseModal from '../Modals/InviteResponseModal';
import { Channel, Topic } from '../../types';
import { useMobileMenu } from '../../context/MobileMenuContext';
import { getAvatarColor, getInitials } from '../../utils/avatar';
import logo from '../../assets/logo.png';

const ITEM_LIMIT = 5;
const SEARCH_THRESHOLD = 10;

interface SidebarProps {
    className?: string;
    activeTab: SidebarTab;
}

export default function Sidebar({ className = '', activeTab }: SidebarProps) {
    const {
        activeView,
        setActiveView,
        currentUser,
        channels,
        departments,
        tickets,
        directMessages,
        topics,
        projects,
        isAdmin,
        setShowSettings,
        setShowCreateChannel,
        setShowCreateTicket,
        setShowCreateProject,
        setShowCreateTopic,
        setShowCreateDM,
        unreadCounts,
        hasPermission,
        hasModuleAccess
    } = useApp();

    const mobileMenu = useMobileMenu();

    const handleNavigate = (view: any) => {
        setActiveView(view);
        mobileMenu?.close();
    };

    const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
    const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
    const [sectionSearch, setSectionSearch] = useState<Record<string, string>>({});
    const [inviteToRespond, setInviteToRespond] = useState<{ item: Channel | Topic; type: 'channel' | 'topic' } | null>(null);

    const toggleGroup = (group: string) => {
        setCollapsedGroups(prev => {
            const newSet = new Set(prev);
            if (newSet.has(group)) {
                newSet.delete(group);
            } else {
                newSet.add(group);
            }
            return newSet;
        });
    };

    const toggleExpand = (section: string) => {
        setExpandedSections(prev => {
            const newSet = new Set(prev);
            if (newSet.has(section)) {
                newSet.delete(section);
            } else {
                newSet.add(section);
            }
            return newSet;
        });
    };

    const isViewActive = (type: string, id: string) => activeView.type === type && activeView.id === id;

    const getUnreadCount = (type: string, id: string): number => {
        if (type === 'channel') {
            return unreadCounts.channel[id] || 0;
        } else if (type === 'dm') {
            return unreadCounts.dm[id] || 0;
        } else if (type === 'topic') {
            return unreadCounts.topic[id] || 0;
        }
        return 0;
    };

    const getDMName = (dm: any) => {
        const otherUser = dm.participants?.find((p: any) => p.id !== currentUser?.id);
        return otherUser ? otherUser.name : 'Unknown User';
    };

    const ticketsByStatus = {
        open: tickets.filter(t => t.status === 'open').length,
        inProgress: tickets.filter(t => t.status === 'in_progress').length,
        resolved: tickets.filter(t => t.status === 'resolved').length
    };

    // Helper to limit and filter items
    const getLimitedItems = <T extends { id: string; name?: string }>(
        items: T[],
        section: string,
        getName: (item: T) => string = (item) => (item as any).name || ''
    ) => {
        const isExpanded = expandedSections.has(section);
        const searchTerm = sectionSearch[section]?.toLowerCase() || '';

        // Sort by last_message_at descending
        const sorted = [...items].sort((a, b) => {
            const timeA = (a as any).last_message_at ? new Date((a as any).last_message_at).getTime() : 0;
            const timeB = (b as any).last_message_at ? new Date((b as any).last_message_at).getTime() : 0;
            return timeB - timeA;
        });

        let filtered = sorted;
        if (searchTerm) {
            filtered = sorted.filter(item => getName(item).toLowerCase().includes(searchTerm));
        }

        if (isExpanded) {
            return filtered;
        }
        return filtered.slice(0, ITEM_LIMIT);
    };

    const renderShowMore = (total: number, section: string) => {
        const isExpanded = expandedSections.has(section);
        const remaining = total - ITEM_LIMIT;

        if (total <= ITEM_LIMIT && !isExpanded) return null;

        return (
            <div
                className="nav-item"
                onClick={() => toggleExpand(section)}
                style={{ opacity: 0.7, fontSize: 12, justifyContent: 'center', color: 'var(--text-muted)' }}
            >
                {isExpanded ? 'Show less' : `Show ${remaining} more`}
            </div>
        );
    };

    const renderSectionSearch = (section: string, total: number) => {
        if (!expandedSections.has(section) || total <= SEARCH_THRESHOLD) return null;

        return (
            <div style={{ padding: '0 12px 8px' }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    background: 'rgba(255,255,255,0.05)',
                    borderRadius: 6,
                    padding: '6px 10px',
                    border: '1px solid rgba(255,255,255,0.05)'
                }}>
                    <Search size={14} style={{ color: 'var(--text-muted)' }} />
                    <input
                        placeholder="Search..."
                        value={sectionSearch[section] || ''}
                        onChange={(e) => setSectionSearch(prev => ({ ...prev, [section]: e.target.value }))}
                        style={{
                            border: 'none',
                            background: 'transparent',
                            outline: 'none',
                            color: 'var(--sidebar-text)',
                            fontSize: 13,
                            width: '100%',
                            fontFamily: 'inherit'
                        }}
                    />
                </div>
            </div>
        );
    };

    // Render Chat tab content
    const renderChatContent = () => (
        <>
            {/* Inbox */}
            <div className="nav-section">
                <div
                    className={`nav-item ${activeView.type === 'inbox' ? 'active' : ''} `}
                    onClick={() => setActiveView({ type: 'inbox', id: 'inbox' })}
                >
                    <span className="nav-item-icon">
                        <Inbox size={18} strokeWidth={1.5} />
                    </span>
                    <span className="nav-item-name">Inbox</span>
                </div>
            </div>

            {/* Groups/Channels */}
            {hasModuleAccess('chat') && (
                <div className="nav-section">
                    <div className="nav-section-header" onClick={() => toggleGroup('channels')}>
                        <span className="nav-section-title">
                            {collapsedGroups.has('channels') ? <ChevronRight size={14} strokeWidth={2} style={{ marginRight: 4 }} /> : <ChevronDown size={14} strokeWidth={2} style={{ marginRight: 4 }} />}
                            Groups
                        </span>
                        <button
                            className="btn btn-ghost btn-icon btn-sm"
                            onClick={(e) => { e.stopPropagation(); setShowCreateChannel(true); }}
                            title="Create group"
                            style={{ opacity: 1, color: 'rgba(255,255,255,0.8)' }}
                        >
                            <Plus size={16} strokeWidth={2} />
                        </button>
                    </div>
                    {!collapsedGroups.has('channels') && (
                        <>
                            {renderSectionSearch('channels', channels.filter(c => !c.is_archived).length)}
                            {getLimitedItems(channels.filter(c => !c.is_archived), 'channels', c => c.name).map(channel => (
                                <div
                                    key={channel.id}
                                    className={`nav-item ${isViewActive('channel', channel.id) ? 'active' : ''} `}
                                    onClick={() => {
                                        if (channel.invite_status === 'pending') {
                                            setInviteToRespond({ item: channel, type: 'channel' });
                                        } else {
                                            handleNavigate({ type: 'channel', id: channel.id });
                                        }
                                    }}
                                    style={channel.invite_status === 'pending' ? { opacity: 0.6 } : {}}
                                >
                                    <span className="nav-item-icon">
                                        {channel.invite_status === 'pending' ? <Clock size={16} className="text-warning" /> : <Hash size={16} strokeWidth={1.5} />}
                                    </span>
                                    <span className="nav-item-name">
                                        {channel.name}
                                        {channel.invite_status === 'pending' && <span style={{ fontSize: 10, marginLeft: 6, background: 'var(--color-warning)', color: '#000', padding: '1px 5px', borderRadius: 4 }}>Pending</span>}
                                    </span>
                                    {channel.invite_status !== 'pending' && getUnreadCount('channel', channel.id) > 0 && (
                                        <span className="nav-item-badge">{getUnreadCount('channel', channel.id)}</span>
                                    )}
                                </div>
                            ))}
                            {channels.some(c => c.is_archived) && (
                                <div style={{ marginTop: 8 }}>
                                    <div style={{ padding: '4px 16px', fontSize: 10, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.05em', fontWeight: 600 }}>Archived</div>
                                    {channels.filter(c => c.is_archived).map(channel => (
                                        <div
                                            key={channel.id}
                                            className={`nav-item ${isViewActive('channel', channel.id) ? 'active' : ''} `}
                                            onClick={() => handleNavigate({ type: 'channel', id: channel.id })}
                                            style={{ opacity: 0.5 }}
                                        >
                                            <span className="nav-item-icon"><Hash size={14} strokeWidth={1.5} /></span>
                                            <span className="nav-item-name">{channel.name}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {renderShowMore(channels.filter(c => !c.is_archived).length, 'channels')}
                        </>
                    )}
                </div>
            )}

            {/* Topics */}
            {hasModuleAccess('chat') && (
                <div className="nav-section">
                    <div className="nav-section-header" onClick={() => toggleGroup('topics')}>
                        <span className="nav-section-title">
                            {collapsedGroups.has('topics') ? <ChevronRight size={14} strokeWidth={2} style={{ marginRight: 4 }} /> : <ChevronDown size={14} strokeWidth={2} style={{ marginRight: 4 }} />}
                            Topics
                        </span>
                        <button
                            className="btn btn-ghost btn-icon btn-sm"
                            onClick={(e) => { e.stopPropagation(); setShowCreateTopic(true); }}
                            title="Create topic"
                            style={{ opacity: 1, color: 'rgba(255,255,255,0.8)' }}
                        >
                            <Plus size={16} strokeWidth={2} />
                        </button>
                    </div>
                    {!collapsedGroups.has('topics') && (
                        <>
                            {renderSectionSearch('topics', topics.filter(t => !t.is_archived).length)}
                            {getLimitedItems(topics.filter(t => !t.is_archived), 'topics', t => t.name).map(topic => (
                                <div
                                    key={topic.id}
                                    className={`nav-item ${isViewActive('topic', topic.id) ? 'active' : ''} `}
                                    onClick={() => {
                                        if (topic.invite_status === 'pending') {
                                            setInviteToRespond({ item: topic, type: 'topic' });
                                        } else {
                                            handleNavigate({ type: 'topic', id: topic.id });
                                        }
                                    }}
                                    style={topic.invite_status === 'pending' ? { opacity: 0.6 } : {}}
                                >
                                    <span className="nav-item-icon">
                                        {topic.invite_status === 'pending' ?
                                            <Clock size={16} className="text-warning" /> :
                                            <Layers size={16} strokeWidth={1.5} style={{ color: topic.color || 'var(--text-secondary)' }} />
                                        }
                                    </span>
                                    <span className="nav-item-name">
                                        {topic.name}
                                        {topic.invite_status === 'pending' && <span style={{ fontSize: 10, marginLeft: 6, background: 'var(--color-warning)', color: '#000', padding: '1px 5px', borderRadius: 4 }}>Pending</span>}
                                    </span>
                                    {topic.invite_status !== 'pending' && getUnreadCount('topic', topic.id) > 0 && (
                                        <span className="nav-item-badge">{getUnreadCount('topic', topic.id)}</span>
                                    )}
                                </div>
                            ))}
                            {topics.some(t => t.is_archived) && (
                                <div style={{ marginTop: 8 }}>
                                    <div style={{ padding: '4px 16px', fontSize: 10, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.05em', fontWeight: 600 }}>Archived</div>
                                    {topics.filter(t => t.is_archived).map(topic => (
                                        <div
                                            key={topic.id}
                                            className={`nav-item ${isViewActive('topic', topic.id) ? 'active' : ''} `}
                                            onClick={() => handleNavigate({ type: 'topic', id: topic.id })}
                                            style={{ opacity: 0.5 }}
                                        >
                                            <span className="nav-item-icon">
                                                <Layers size={14} strokeWidth={1.5} style={{ color: topic.color || 'var(--text-secondary)' }} />
                                            </span>
                                            <span className="nav-item-name">{topic.name}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {renderShowMore(topics.filter(t => !t.is_archived).length, 'topics')}
                        </>
                    )}
                </div>
            )}

            {/* Direct Messages */}
            {hasModuleAccess('chat') && (
                <div className="nav-section">
                    <div className="nav-section-header" onClick={() => toggleGroup('dms')}>
                        <span className="nav-section-title">
                            {collapsedGroups.has('dms') ? <ChevronRight size={14} strokeWidth={2} style={{ marginRight: 4 }} /> : <ChevronDown size={14} strokeWidth={2} style={{ marginRight: 4 }} />}
                            Direct Messages
                        </span>
                        <button
                            className="btn btn-ghost btn-icon btn-sm"
                            onClick={(e) => { e.stopPropagation(); setShowCreateDM(true); }}
                            title="New message"
                            style={{ opacity: 1, color: 'rgba(255,255,255,0.8)' }}
                        >
                            <Plus size={16} strokeWidth={2} />
                        </button>
                    </div>
                    {!collapsedGroups.has('dms') && (
                        <>
                            {renderSectionSearch('dms', directMessages.length)}
                            {getLimitedItems(directMessages, 'dms', getDMName).map(dm => {
                                const dmName = getDMName(dm);
                                const otherUser = dm.participants?.find((p: any) => p.id !== currentUser?.id);
                                return (
                                    <div
                                        key={dm.id}
                                        className={`nav-item ${isViewActive('dm', dm.id) ? 'active' : ''} `}
                                        onClick={() => handleNavigate({ type: 'dm', id: dm.id })}
                                    >
                                        <div className="avatar avatar-sm" style={{ position: 'relative', background: getAvatarColor(otherUser?.id), width: 20, height: 20, fontSize: 10, borderRadius: 6 }}>
                                            {getInitials(otherUser?.name || dmName)}
                                            <span className={`presence-dot ${otherUser?.status_type || 'offline'}`} style={{ position: 'absolute', bottom: -2, right: -2, width: 8, height: 8, border: '2px solid var(--sidebar-bg)' }} />
                                        </div>
                                        <span className="nav-item-name">{dmName}</span>
                                        {getUnreadCount('dm', dm.id) > 0 && (
                                            <span className="nav-item-badge">{getUnreadCount('dm', dm.id)}</span>
                                        )}
                                    </div>
                                );
                            })}
                            {renderShowMore(directMessages.length, 'dms')}
                        </>
                    )}
                </div>
            )}
        </>
    );

    // Render Work tab content
    const renderWorkContent = () => (
        <>
            {hasModuleAccess('tickets') && (
                <div className="nav-section">
                    <div className="nav-section-header" onClick={() => toggleGroup('tickets')}>
                        <span className="nav-section-title">
                            {collapsedGroups.has('tickets') ? <ChevronRight size={14} strokeWidth={2} style={{ marginRight: 4 }} /> : <ChevronDown size={14} strokeWidth={2} style={{ marginRight: 4 }} />}
                            Tickets
                        </span>
                        <button
                            className="btn btn-ghost btn-icon btn-sm"
                            onClick={(e) => { e.stopPropagation(); setShowCreateTicket(true); }}
                            title="Create ticket"
                            style={{ opacity: 1, color: 'rgba(255,255,255,0.8)' }}
                        >
                            <Plus size={16} strokeWidth={2} />
                        </button>
                    </div>
                    {!collapsedGroups.has('tickets') && (
                        <>
                            <div
                                className={`nav-item ${activeView.type === 'tickets-list' ? 'active' : ''} `}
                                onClick={() => handleNavigate({ type: 'tickets-list', id: 'all' })}
                            >
                                <span className="nav-item-icon">
                                    <Layers size={18} strokeWidth={1.5} />
                                </span>
                                <span className="nav-item-name">All Tickets</span>
                                {ticketsByStatus.open + ticketsByStatus.inProgress > 0 && (
                                    <span className="nav-item-badge">{ticketsByStatus.open + ticketsByStatus.inProgress}</span>
                                )}
                            </div>
                            <div
                                className={`nav-item ${activeView.type === 'ticket-calendar' ? 'active' : ''} `}
                                onClick={() => handleNavigate({ type: 'ticket-calendar', id: 'all' })}
                            >
                                <span className="nav-item-icon">
                                    <Calendar size={18} strokeWidth={1.5} />
                                </span>
                                <span className="nav-item-name">Calendar</span>
                            </div>
                            <div
                                className={`nav-item ${activeView.type === 'ticket-board' ? 'active' : ''} `}
                                onClick={() => handleNavigate({ type: 'ticket-board', id: 'all' })}
                            >
                                <span className="nav-item-icon">
                                    <Columns3 size={18} strokeWidth={1.5} />
                                </span>
                                <span className="nav-item-name">Board</span>
                            </div>
                            {hasPermission('view:ticket_dashboard') && (
                                <div
                                    className={`nav-item ${activeView.type === 'ticket-dashboard' ? 'active' : ''} `}
                                    onClick={() => handleNavigate({ type: 'ticket-dashboard', id: 'all' })}
                                >
                                    <span className="nav-item-icon">
                                        <LayoutDashboard size={18} strokeWidth={1.5} />
                                    </span>
                                    <span className="nav-item-name">Dashboard</span>
                                </div>
                            )}
                            {/* Department Queue - visible to dept heads */}
                            {departments.some(d => d.head_ids?.includes(currentUser?.id || '')) && (
                                <div
                                    className={`nav-item ${activeView.type === 'dept-queue' ? 'active' : ''} `}
                                    onClick={() => handleNavigate({ type: 'dept-queue', id: 'queue' })}
                                >
                                    <span className="nav-item-icon">
                                        <Inbox size={18} strokeWidth={1.5} />
                                    </span>
                                    <span className="nav-item-name">Dept Queue</span>
                                </div>
                            )}
                            <div style={{ height: 1, background: 'rgba(255,255,255,0.05)', margin: '8px 16px' }} />
                            {tickets.slice(0, ITEM_LIMIT).map(ticket => (
                                <div
                                    key={ticket.id}
                                    className={`nav-item ${isViewActive('ticket', ticket.id) ? 'active' : ''} `}
                                    onClick={() => handleNavigate({ type: 'ticket', id: ticket.id })}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden' }}>
                                        <span style={{ color: ticket.status === 'open' ? '#10b981' : '#94a3b8', flexShrink: 0, fontSize: 8 }}>●</span>
                                        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ticket.title}</span>
                                    </div>
                                </div>
                            ))}
                        </>
                    )}
                </div>
            )}
            {hasModuleAccess('tickets') && (
                <div className="nav-section">
                    <div className="nav-section-header" onClick={() => toggleGroup('projects')}>
                        <span className="nav-section-title">
                            {collapsedGroups.has('projects') ? <ChevronRight size={14} strokeWidth={2} style={{ marginRight: 4 }} /> : <ChevronDown size={14} strokeWidth={2} style={{ marginRight: 4 }} />}
                            Projects
                        </span>
                        <button className="btn btn-ghost btn-icon btn-sm" onClick={event => { event.stopPropagation(); setShowCreateProject(true); }} title="Create project" style={{ opacity: 1, color: 'rgba(255,255,255,0.8)' }}>
                            <Plus size={16} strokeWidth={2} />
                        </button>
                    </div>
                    {!collapsedGroups.has('projects') && projects.map(project => (
                        <div key={project.id} className={`nav-item ${isViewActive('project-list', project.id) || isViewActive('ticket-board', project.id) ? 'active' : ''}`}
                            onClick={() => handleNavigate({ type: 'project-list', id: project.id })}>
                            <span className="nav-item-icon" style={{ color: project.color }}><FolderKanban size={18} strokeWidth={1.7} /></span>
                            <span className="nav-item-name">{project.name}</span>
                            {project.ticket_count > 0 && <span className="nav-item-badge">{project.ticket_count}</span>}
                        </div>
                    ))}
                    {!collapsedGroups.has('projects') && projects.length === 0 && (
                        <button className="nav-item" onClick={() => setShowCreateProject(true)} style={{ width: '100%', border: 0, color: 'rgba(255,255,255,0.6)' }}>
                            <span className="nav-item-icon"><Plus size={16} /></span><span className="nav-item-name">Create your first project</span>
                        </button>
                    )}
                </div>
            )}
        </>
    );

    // Render Admin tab content
    const renderAdminContent = () => {
        // Allow access if: full admin, super admin, OR any granular admin tab permission
        const hasAnyAdminAccess = isAdmin
            || hasPermission('super:admin')
            || hasPermission('admin:view:roles')
            || hasPermission('admin:view:users')
            || hasPermission('admin:view:departments')
            || hasPermission('admin:view:settings')
            || hasPermission('admin:view:security')
            || hasPermission('admin:view:audit');

        return (
            <>
                {hasAnyAdminAccess && (
                    <div className="nav-section">
                        <div
                            className={`nav-item ${activeView.type === 'admin' ? 'active' : ''} `}
                            onClick={() => setActiveView({ type: 'admin', id: 'admin' })}
                        >
                            <span className="nav-item-icon">
                                <Shield size={18} strokeWidth={1.5} />
                            </span>
                            <span className="nav-item-name">Admin Dashboard</span>
                        </div>
                    </div>
                )}
            </>
        );
    };


    return (
        <aside className={`sidebar ${className} `}>
            <header className="sidebar-header">
                <div className="workspace-branding">
                    <div className="workspace-logo">
                        <img src={logo} alt="Tock Logo" />
                    </div>
                    <span style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-inverse)', whiteSpace: 'nowrap' }}>BakeDeco</span>
                </div>
                <button
                    className="btn btn-ghost btn-icon"
                    title="Profile & Preferences"
                    onClick={() => setShowSettings(true)}
                    style={{ color: 'var(--sidebar-text)' }}
                >
                    <Settings size={18} strokeWidth={1.5} />
                </button>
            </header>

            <nav className="sidebar-nav">
                {activeTab === 'chat' && renderChatContent()}
                {activeTab === 'work' && renderWorkContent()}
                {activeTab === 'admin' && renderAdminContent()}
            </nav>

            {/* Current User */}
            {currentUser && (
                <div className="sidebar-footer" style={{ borderTop: '1px solid var(--sidebar-border)', padding: '16px 20px', background: 'rgba(0,0,0,0.1)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 36, height: 36, background: 'var(--accent-primary)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 'bold', fontSize: 14 }}>
                            {getInitials(currentUser.name)}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--sidebar-text-hover)' }}>{currentUser.name}</div>
                            <div style={{ fontSize: 12, color: 'var(--sidebar-text)' }}>
                                <StatusSelector />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <InviteResponseModal
                isOpen={!!inviteToRespond}
                onClose={() => setInviteToRespond(null)}
                item={inviteToRespond?.item || null}
                type={inviteToRespond?.type || 'topic'}
            />
        </aside>
    );
}
